<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import {
    TABLE_SEATS_MAX,
    TABLE_SEATS_MIN,
    selectionSummary,
  } from '@/domain/reservations/reservation-tables-draft';
  import type { IReservationTableRef } from '@/interfaces/IReservation';
  import type { TTableReservationsMode } from '@/interfaces/IReservationSettings';

  /**
   * Sticky bar under the plan for the tables the owner tapped. Seats mode
   * changes seats and bookability together (D15: «−» / «+» are relative);
   * groups mode only offers to join the selection.
   */
  const props = defineProps<{
    selection: IReservationTableRef[];
    modeControl: 'toggle' | 'segmented';
    mode: 'seats' | 'groups';
    joinEnabled?: boolean;
    joinSeats?: number;
    disabled?: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'adjust', delta: 1 | -1): void;
    (e: 'toggle-off'): void;
    (e: 'mode', mode: TTableReservationsMode): void;
    (e: 'join'): void;
    (e: 'done'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const MODES: readonly TTableReservationsMode[] = [
    'online',
    'internal',
    'none',
  ];
  const modeItems = computed(() =>
    MODES.map((id) => ({
      id,
      label: tr(`reservations.tables.mode.${id}`),
    }))
  );

  const summary = computed(() => selectionSummary(props.selection));
  const countLabel = computed(() =>
    String(
      proxy.$tc(
        'reservations.tables.editor.selectedMany',
        summary.value.count,
        { count: summary.value.count }
      )
    )
  );
  const title = computed(() => {
    if (props.mode === 'groups') {
      if (!summary.value.count)
        return tr('reservations.guide.groups.pickOnline');
      // The seats total decides the combination, so it lives in the title, never truncated away.
      return props.joinSeats && summary.value.count >= 2
        ? `${countLabel.value} · ${tr('reservations.guide.groups.selectedSeats', {
            count: props.joinSeats,
          })}`
        : countLabel.value;
    }
    return summary.value.count === 1
      ? summary.value.names[0]
      : countLabel.value;
  });
  const subtitle = computed(() => {
    if (!summary.value.count) return '';
    if (props.mode === 'groups') return summary.value.names.join(', ');
    return summary.value.count === 1
      ? tr('reservations.tables.editor.selectedOne')
      : summary.value.names.join(', ');
  });
  /** What assistive tech hears for the seats value («…» and «—» say nothing aloud). */
  const seatsValueLabel = computed(() => {
    const value = summary.value.allOff
      ? tr('reservations.tables.editor.off')
      : summary.value.capacity === null
        ? tr('reservations.tables.editor.seatsMixed')
        : String(summary.value.capacity);
    return `${tr('reservations.tables.editor.seats')}: ${value}`;
  });
  /** «…» when the selection mixes seats, «—» when every table is off. */
  const seatsValue = computed(() => {
    if (summary.value.allOff) return '—';
    return summary.value.capacity === null
      ? '…'
      : String(summary.value.capacity);
  });
  // At the limits the buttons go quiet; a mixed or all-off selection keeps both
  // (D15: on an off table either button only turns it online).
  const canDecrease = computed(
    () =>
      summary.value.allOff ||
      summary.value.capacity === null ||
      summary.value.capacity > TABLE_SEATS_MIN
  );
  const canIncrease = computed(
    () =>
      summary.value.allOff ||
      summary.value.capacity === null ||
      summary.value.capacity < TABLE_SEATS_MAX
  );
  /** The common mode of the selection, or nothing when it is mixed. */
  const commonMode = computed(() => {
    const first = props.selection[0];
    if (!first) return '';
    return props.selection.every(
      (table) => table.reservationsMode === first.reservationsMode
    )
      ? first.reservationsMode
      : '';
  });

  function selectMode(id: string) {
    const mode = MODES.find((candidate) => candidate === id);
    if (mode) emit('mode', mode);
  }
</script>

<template>
  <div
    class="reservation-table-selection-bar"
    :class="`mode-${mode}`"
    role="group"
    :aria-label="$t('reservations.tables.editor.selectionLabel')"
  >
    <div class="bar-summary">
      <p class="summary-title">{{ title }}</p>
      <p v-if="subtitle" class="summary-subtitle">{{ subtitle }}</p>
    </div>
    <div v-if="mode === 'groups'" class="bar-controls">
      <PosUiButton
        color="neutral"
        variant="ghost"
        :disabled="disabled"
        @click="emit('done')"
        >{{ $t('cancel') }}</PosUiButton
      >
      <PosUiButton
        color="success"
        variant="soft"
        icon="link-variant"
        :disabled="disabled || !joinEnabled"
        @click="emit('join')"
        >{{ $t('reservations.guide.groups.join') }}</PosUiButton
      >
    </div>
    <div v-else class="bar-controls">
      <div class="seats-control">
        <span class="seats-label">{{
          $t('reservations.tables.editor.seats')
        }}</span>
        <PosUiButton
          color="neutral"
          variant="outline"
          icon="minus"
          :aria-label="$t('reservations.tables.editor.fewer')"
          :disabled="disabled || !canDecrease"
          @click="emit('adjust', -1)"
        />
        <output class="seats-value" :aria-label="seatsValueLabel">{{
          seatsValue
        }}</output>
        <PosUiButton
          color="neutral"
          variant="outline"
          icon="plus"
          :aria-label="$t('reservations.tables.editor.more')"
          :disabled="disabled || !canIncrease"
          @click="emit('adjust', 1)"
        />
      </div>
      <!-- Below sm this control takes its own line; «Listo» stays on the seats line. -->
      <div class="mode-control">
        <template v-if="modeControl === 'segmented'">
          <span class="mode-label">{{
            $t('reservations.tables.editor.use')
          }}</span>
          <PosUiSegmented
            class="mode-segmented"
            :value="commonMode"
            :items="modeItems"
            :disabled="disabled"
            block
            :aria-label="$t('reservations.tables.editor.use')"
            @input="selectMode"
          />
        </template>
        <PosUiButton
          v-else
          class="off-toggle"
          :class="{ pressed: summary.allOff }"
          color="neutral"
          variant="outline"
          :icon="summary.allOff ? 'check' : undefined"
          :aria-pressed="summary.allOff ? 'true' : 'false'"
          :disabled="disabled"
          @click="emit('toggle-off')"
          >{{ $t('reservations.tables.editor.off') }}</PosUiButton
        >
      </div>
      <PosUiButton
        class="done-button"
        color="neutral"
        variant="outline"
        :disabled="disabled"
        @click="emit('done')"
        >{{ $t('reservations.guide.done') }}</PosUiButton
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .reservation-table-selection-bar {
    // Sticky at the bottom of whichever container scrolls (guide body or settings).
    @apply tw-sticky tw-bottom-0 tw-z-10 tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border-2 tw-border-solid tw-border-orange-600 tw-bg-white tw-p-3 tw-shadow-sm sm:tw-flex-row sm:tw-flex-wrap sm:tw-items-center sm:tw-justify-between;
    // Never narrower than 12rem: the controls wrap under the summary instead of hiding the name.
    .bar-summary {
      @apply tw-min-w-0 tw-flex-1 sm:tw-basis-48;
      p {
        @apply tw-m-0;
      }
      .summary-title {
        @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
      }
      // Hints and member names are content: two lines, then an ellipsis.
      .summary-subtitle {
        @apply tw-mt-0.5 tw-line-clamp-2 tw-break-words tw-text-xs tw-text-neutral-500;
      }
    }
    // Two rows below sm: [seats … Listo] then the mode control full width;
    // one row from sm up: [seats] [mode] [Listo] (flex order swaps them).
    .bar-controls {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 sm:tw-ml-auto sm:tw-justify-end;
    }
    .seats-control {
      @apply tw-order-1 tw-flex tw-items-center tw-gap-1;
      .seats-label {
        @apply tw-mr-1 tw-text-sm tw-text-neutral-600;
      }
      // Icon-only buttons: square 44px targets.
      :deep(.pos-ui-button) {
        @apply tw-w-11 tw-px-0;
      }
    }
    .seats-value {
      @apply tw-min-w-[2.5rem] tw-text-center tw-text-base tw-font-semibold tw-tabular-nums tw-text-neutral-900;
    }
    .done-button {
      @apply tw-order-2 tw-ml-auto sm:tw-order-3 sm:tw-ml-0;
    }
    .mode-control {
      @apply tw-order-3 tw-flex tw-w-full tw-items-center tw-gap-2 sm:tw-order-2 sm:tw-w-auto;
      .mode-label {
        @apply tw-hidden tw-text-sm tw-text-neutral-600 sm:tw-inline;
      }
      // Block fills the phone row; from sm the control shrinks to its content.
      .mode-segmented {
        @apply sm:tw-w-auto;
      }
      :deep(.pos-ui-segmented-option) {
        @apply tw-h-auto tw-min-h-10 tw-whitespace-normal tw-px-2 tw-py-1 sm:tw-flex-none;
      }
      .off-toggle {
        @apply tw-self-start;
      }
      // Pressed = the selection is not bookable: a dark chip, like active
      // filters. Nested deep enough to beat the button's own variant rules.
      .off-toggle.pressed {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-white;
        &:hover {
          @apply tw-border-neutral-900 tw-bg-neutral-800 tw-text-white;
        }
      }
    }
    &.mode-groups .bar-controls {
      @apply tw-justify-end sm:tw-shrink-0;
    }
  }
</style>
